/**
 * useStudioDesk.ts
 *
 * Hook for fetching the caller's Studio Desk aggregate (Resume / Needs
 * attention / per-type counts) from `GET /studio/desk` (schema-service).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import type { StudioDesk } from 'types/studio';
import { getStudioDesk, type GetStudioDeskParams } from '../api/studioApi';

const EMPTY_DESK: StudioDesk = {
  recents: [],
  findings: [],
  counts: {},
  generatedAt: '',
  degraded: [],
};

export interface UseStudioDeskReturn {
  desk: StudioDesk;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the Studio Desk aggregate. Callers that are not in `platform` mode
 * should not call this hook at all — the desk is a platform-mode-only
 * surface (`GET /studio/desk` is never called from the operator placeholder).
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param params - Optional bounds; server falls back to dynamic config
 */
export function useStudioDesk(
  apiClients: ApiClients | null,
  params: GetStudioDeskParams = {}
): UseStudioDeskReturn {
  const [desk, setDesk] = useState<StudioDesk>(EMPTY_DESK);
  // Starts `true`: the fetch is kicked off by the effect below, so a `false` initial value would
  // make the first paint render the "nothing yet" empty state (and fire the desk-loaded callback
  // with an empty desk) before the request has even been issued.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const { recentLimit, staleDays, kinds } = params;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchDesk = useCallback(async () => {
    if (!apiClients?.schemaClient) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getStudioDesk(apiClients.schemaClient, { recentLimit, staleDays, kinds });
      if (isMountedRef.current) {
        setDesk(data);
      }
    } catch (err: unknown) {
      if (isMountedRef.current) {
        // Fail closed: drop whatever was rendered rather than leaving a previous (possibly
        // previous-identity) desk on screen next to an error.
        setDesk(EMPTY_DESK);
        setError(err instanceof Error ? err.message : 'Failed to fetch Studio Desk');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients, recentLimit, staleDays, kinds?.join(',')]);

  useEffect(() => {
    if (!apiClients) {
      setDesk(EMPTY_DESK);
      setLoading(false);
      return;
    }

    // Clear before re-fetching. `fetchDesk` changes identity whenever `apiClients` does (logout,
    // impersonation switch) or the bounds change, so one identity can never see the previous
    // identity's Resume list while the new request is in flight.
    setDesk(EMPTY_DESK);
    fetchDesk();
  }, [apiClients, fetchDesk]);

  const refetch = fetchDesk;

  return { desk, loading, error, refetch };
}
